import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreatorCrossService from "../../../creator/service/CreatorCrossService";
import StatsCrossService from "../../../services/StatsCrossService";
import { getAdminContentLanguage } from "../../../services/LangPrefs";
import PublishingPolicyDialog from "../../pzl_common/board/PublishingPolicyDialog";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import CommonTagByStateStatsPanel from "../../pzl_common/board/CommonTagByStateStatsPanel";
import StateStatsCard from "../../pzl_common/board/StateStatsCard";
import AdminCrossBoardService, { DRAFT, PUBLISHED, READY_TO_REVIEW, REVIEWED } from "./AdminCrossBoardService";
import Logger from "../../../services/Logger";
import Step0SelectDraft from "./Step0_SelectDraft";

const DISPLAY_STATES = [DRAFT, READY_TO_REVIEW, REVIEWED, PUBLISHED];
const STATS_VIEW_MODE_KEY = "admin.cross.statsViewMode";
const STATS_VIEW_MODE_VERSION_KEY = "admin.cross.statsViewMode.v2";

/** 1 = four overview cards, 2 = common tag by state, 3 = merged low-count tags. Migrates legacy v where only [1][2] existed and 2 meant tags → now 3. */
function loadInitialStatsViewMode() {
    try {
        if (localStorage.getItem(STATS_VIEW_MODE_VERSION_KEY) !== "1") {
            const prev = localStorage.getItem(STATS_VIEW_MODE_KEY);
            if (prev === "2") {
                localStorage.setItem(STATS_VIEW_MODE_KEY, "3");
            }
            localStorage.setItem(STATS_VIEW_MODE_VERSION_KEY, "1");
        }
        const v = localStorage.getItem(STATS_VIEW_MODE_KEY);
        if (v === "1" || v === "2" || v === "3") {
            return parseInt(v, 10);
        }
    } catch (e) {}
    return 1;
}

function mergeStateStats(stats, stateKeys) {
    const merged = {
        countByDifficulty: { total: 0, EASY: 0, MEDIUM: 0, HARD: 0 },
        countByType: {},
        countByTag: {}
    };
    stateKeys.forEach((key) => {
        const d = stats[key];
        if (!d) return;
        const diff = d.countByDifficulty || {};
        merged.countByDifficulty.total += diff.total ?? 0;
        merged.countByDifficulty.EASY += diff.EASY ?? 0;
        merged.countByDifficulty.MEDIUM += diff.MEDIUM ?? 0;
        merged.countByDifficulty.HARD += diff.HARD ?? 0;
        Object.entries(d.countByType || {}).forEach(([type, fp]) => {
            if (!merged.countByType[type]) merged.countByType[type] = { PAID: 0, FREE: 0 };
            const o = typeof fp === "object" && fp ? fp : {};
            merged.countByType[type].PAID += o.PAID ?? 0;
            merged.countByType[type].FREE += o.FREE ?? 0;
        });
        Object.entries(d.countByTag || {}).forEach(([tag, count]) => {
            merged.countByTag[tag] = (merged.countByTag[tag] || 0) + (typeof count === "number" ? count : 0);
        });
    });
    return merged;
}

const steps = [
    { label: "Draft", state: DRAFT },
    { label: "Ready to review", state: READY_TO_REVIEW },
    { label: "Reviewed", state: REVIEWED },
    { label: "Published", state: PUBLISHED },
];

// localStorage keys for filter options
const FILTER_PAYMENT_KEY = "admin.cross.filter.payment";
const FILTER_TAGS_KEY = "admin.cross.filter.tags";
const FILTER_LEVEL_KEY = "admin.cross.filter.level";
const FILTER_KEYWORD_KEY = "admin.cross.filter.keyword";

// Helper functions to load filter options from localStorage
const loadFilterPayment = () => {
    try {
        const stored = localStorage.getItem(FILTER_PAYMENT_KEY);
        if (stored === null) return null;
        if (stored === "all") return null;
        return stored === "true" || stored === "free";
    } catch (error) {
        Logger.error('Error reading filter payment from localStorage:', error);
        return null;
    }
};

const loadFilterTags = () => {
    try {
        const stored = localStorage.getItem(FILTER_TAGS_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        Logger.error('Error reading filter tags from localStorage:', error);
        return [];
    }
};

const loadFilterLevel = () => {
    try {
        const stored = localStorage.getItem(FILTER_LEVEL_KEY);
        return stored || "all";
    } catch (error) {
        Logger.error('Error reading filter level from localStorage:', error);
        return "all";
    }
};

const loadFilterKeyword = () => {
    try {
        const stored = localStorage.getItem(FILTER_KEYWORD_KEY);
        return stored != null ? String(stored) : "";
    } catch (error) {
        Logger.error('Error reading filter keyword from localStorage:', error);
        return "";
    }
};

// Helper functions to save filter options to localStorage
const saveFilterPayment = (payment) => {
    try {
        if (payment === null) {
            localStorage.setItem(FILTER_PAYMENT_KEY, "all");
        } else {
            localStorage.setItem(FILTER_PAYMENT_KEY, payment === true ? "free" : "paid");
        }
    } catch (error) {
        Logger.error('Error saving filter payment to localStorage:', error);
    }
};

const saveFilterTags = (tags) => {
    try {
        if (tags && Array.isArray(tags) && tags.length > 0) {
            localStorage.setItem(FILTER_TAGS_KEY, JSON.stringify(tags));
        } else {
            localStorage.removeItem(FILTER_TAGS_KEY);
        }
    } catch (error) {
        Logger.error('Error saving filter tags to localStorage:', error);
    }
};

const saveFilterLevel = (level) => {
    try {
        localStorage.setItem(FILTER_LEVEL_KEY, level || "all");
    } catch (error) {
        Logger.error('Error saving filter level to localStorage:', error);
    }
};

const saveFilterKeyword = (keyword) => {
    try {
        if (keyword != null && String(keyword).trim() !== "") {
            localStorage.setItem(FILTER_KEYWORD_KEY, String(keyword).trim());
        } else {
            localStorage.removeItem(FILTER_KEYWORD_KEY);
        }
    } catch (error) {
        Logger.error('Error saving filter keyword to localStorage:', error);
    }
};

export default function AdminCrossBoardMainMain() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Initialize activeStep from URL parameter if present, otherwise default to 0
    const getInitialStep = () => {
        const activeStepParam = searchParams.get('activeStep');
        if (activeStepParam !== null) {
            const stepIndex = parseInt(activeStepParam, 10);
            if (stepIndex >= 0 && stepIndex < steps.length) {
                return stepIndex;
            }
        }
        return 0;
    };
    
    const [activeStep, setActiveStep] = useState(getInitialStep);

    // State for group-by-state stats
    const [groupByStateStats, setGroupByStateStats] = useState(null);
    const [groupByStateLoading, setGroupByStateLoading] = useState(false);

    // Tab 2: GET /v1/stats/cross/tag-counts-by-state (common tag per state)
    const [commonTagCountsApiData, setCommonTagCountsApiData] = useState(undefined);
    const [commonTagCountsApiLoading, setCommonTagCountsApiLoading] = useState(false);
    const [commonTagCountsApiFailed, setCommonTagCountsApiFailed] = useState(false);

    // State for tags, level, and free/paid filters - load from localStorage on mount
    const [selectedTags, setSelectedTags] = useState(() => {
        try {
            const tagParam = new URLSearchParams(window.location.search).get("tag");
            if (tagParam && tagParam.trim()) {
                const tags = [tagParam.trim()];
                saveFilterTags(tags);
                return tags;
            }
        } catch (error) {
            Logger.error("Error reading tag from URL:", error);
        }
        return loadFilterTags();
    });
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(loadFilterLevel);
    const [selectedFree, setSelectedFree] = useState(loadFilterPayment);
    const [savedKeyword, setSavedKeyword] = useState(loadFilterKeyword);

    // State for policy dialog
    const [policyDialogOpen, setPolicyDialogOpen] = useState(false);

    const [statsViewMode, setStatsViewMode] = useState(loadInitialStatsViewMode);
    const setStatsViewModeAndSave = (mode) => {
        setStatsViewMode(mode);
        try {
            localStorage.setItem(STATS_VIEW_MODE_KEY, String(mode));
        } catch (e) {}
    };

    // When leaving DRAFT step, switch to overview (tab 1); tabs 2–3 are draft-only
    useEffect(() => {
        if (activeStep !== 0 && statsViewMode !== 1) {
            setStatsViewMode(1);
        }
    }, [activeStep, statsViewMode]);

    // When returning to DRAFT, restore view mode from localStorage
    useEffect(() => {
        if (activeStep === 0) {
            try {
                const v = localStorage.getItem(STATS_VIEW_MODE_KEY);
                if (v === "1" || v === "2" || v === "3") {
                    setStatsViewMode(parseInt(v, 10));
                }
            } catch (e) {}
        }
    }, [activeStep]);

    // Fetch group-by-state stats on component mount
    useEffect(() => {
        const fetchGroupByState = async () => {
            setGroupByStateLoading(true);
            try {
                const language = getAdminContentLanguage();
                const result = await StatsCrossService.countByStateGrouped("CROSSWORD", language);
                setGroupByStateStats(result || {});
            } catch (error) {
                Logger.error("Error fetching group-by-state stats:", error);
                setGroupByStateStats({});
            } finally {
                setGroupByStateLoading(false);
            }
        };

        fetchGroupByState();
    }, []);

    useEffect(() => {
        if (activeStep !== 0 || statsViewMode !== 2) {
            return;
        }
        let cancelled = false;
        const run = async () => {
            setCommonTagCountsApiData(undefined);
            setCommonTagCountsApiFailed(false);
            setCommonTagCountsApiLoading(true);
            try {
                const language = getAdminContentLanguage();
                const data = await StatsCrossService.getTagCountsByState("common", "CROSSWORD", language);
                if (!cancelled) {
                    setCommonTagCountsApiData(data && typeof data === "object" ? data : {});
                }
            } catch (error) {
                Logger.error("Error fetching tag counts by state:", error);
                if (!cancelled) {
                    setCommonTagCountsApiFailed(true);
                    setCommonTagCountsApiData(undefined);
                }
            } finally {
                if (!cancelled) {
                    setCommonTagCountsApiLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [activeStep, statsViewMode]);

    useEffect(() => {
        const tagParam = searchParams.get("tag");
        if (!tagParam || !tagParam.trim()) {
            return;
        }
        const next = [tagParam.trim()];
        setSelectedTags((prev) => {
            if (prev.length === 1 && prev[0] === next[0]) {
                return prev;
            }
            saveFilterTags(next);
            return next;
        });
    }, [searchParams]);

    const fetchAvailableTags = useCallback(async () => {
        const tagLabel = (item) => {
            if (typeof item === "string") return item;
            if (item != null && typeof item === "object") {
                return String(item.tag ?? item.name ?? "");
            }
            return "";
        };
        setTagsLoading(true);
        try {
            const state = steps[activeStep]?.state ?? undefined;
            const language = getAdminContentLanguage();
            const tags = await CreatorCrossService.getCrossTags(language, state);
            const list = Array.isArray(tags) ? tags : [];
            const sorted = [...list].sort((a, b) =>
                tagLabel(a).localeCompare(tagLabel(b), undefined, { sensitivity: "base" }),
            );
            setAvailableTags(sorted);
        } catch (error) {
            Logger.error('Error fetching tags:', error);
            setAvailableTags([]);
        } finally {
            setTagsLoading(false);
        }
    }, [activeStep]);

    useEffect(() => {
        fetchAvailableTags();
    }, [fetchAvailableTags]);

    // Handle tags filter change with localStorage persistence
    const handleTagsChange = (newTags) => {
        setSelectedTags(newTags);
        saveFilterTags(newTags);
    };

    // Handle level filter change with localStorage persistence
    const handleLevelChange = (newLevel) => {
        setSelectedLevel(newLevel);
        saveFilterLevel(newLevel);
    };

    // Handle free/paid filter change with localStorage persistence
    const handleFreeChange = (newFree) => {
        setSelectedFree(newFree);
        saveFilterPayment(newFree);
    };

    // Stable callback so Step0SelectDraft's persist effect does not re-run on every parent re-render (avoids flicker / repeated API calls)
    const handleKeywordChange = useCallback((value) => {
        setSavedKeyword(value ?? "");
        saveFilterKeyword(value);
    }, []);

    // Handle URL parameters for activeStep
    useEffect(() => {
        const activeStepParam = searchParams.get('activeStep');
        if (activeStepParam !== null) {
            const stepIndex = parseInt(activeStepParam, 10);
            if (stepIndex >= 0 && stepIndex < steps.length && stepIndex !== activeStep) {
                setActiveStep(stepIndex);
            }
        }
    }, [searchParams, activeStep]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };


    const handleStepClick = (stepIndex) => {
        // Only update if step actually changed
        if (stepIndex === activeStep) {
            return;
        }
        
        // Update URL to reflect the new step
        navigate(`/admin/puzzle/cross_board?activeStep=${stepIndex}`, { replace: true });
        
        // Set active step
        setActiveStep(stepIndex);
    };

    const handlePuzzleClick = (transformedPuzzle) => {
        const puzzle = transformedPuzzle;

        // Navigate to the appropriate step page based on puzzle state
        const stateRouteMap = {
            [DRAFT]: `/admin/puzzle/cross_board/draft/${puzzle.id}`,
            [READY_TO_REVIEW]: `/admin/puzzle/cross_board/ready-to-review/${puzzle.id}`,
            [REVIEWED]: `/admin/puzzle/cross_board/reviewed/${puzzle.id}`,
            [PUBLISHED]: `/admin/puzzle/cross_board/published/${puzzle.id}`,

        };

        const route = stateRouteMap[puzzle.state] || `/admin/puzzle/cross_board/draft/${puzzle.id}`;
        // Add the current activeStep as a parameter so the back button knows where to return
        navigate(`${route}?fromStep=${activeStep}`);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <PuzzleWorkflowStepper
                currentState={steps[activeStep].state}
                onStepClick={handleStepClick}
                size="small"
                align="left"
                puzzleType="CROSSWORD"
            />

            <Box>
                {/* Group-by-state stats: 1 = four cards, 2 = common tag by state, 3 = merged tags (draft step only for 2–3) */}
                <Box sx={{ mb: 3, display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            flexShrink: 0
                        }}
                    >
                        <Button
                            variant={statsViewMode === 1 ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setStatsViewModeAndSave(1)}
                            sx={{ minWidth: 36 }}
                            title="Overview: counts by state"
                        >
                            1
                        </Button>
                        {activeStep === 0 && (
                            <>
                                <Button
                                    variant={statsViewMode === 2 ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => setStatsViewModeAndSave(2)}
                                    sx={{ minWidth: 36 }}
                                    title="'common' tag counts by state"
                                >
                                    2
                                </Button>
                                <Button
                                    variant={statsViewMode === 3 ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => setStatsViewModeAndSave(3)}
                                    sx={{ minWidth: 36 }}
                                    title="Tags merged (ready / reviewed / published)"
                                >
                                    3
                                </Button>
                            </>
                        )}
                    </Box>
                    {groupByStateLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                                Loading stats...
                            </Typography>
                        </Box>
                    ) : groupByStateStats && Object.keys(groupByStateStats).length > 0 ? (
                        <Grid container spacing={2} sx={{ flex: 1 }}>
                            {statsViewMode === 1 &&
                                DISPLAY_STATES.map((state) => (
                                    <StateStatsCard
                                        key={state}
                                        state={state}
                                        stateData={groupByStateStats[state]}
                                        typesFilter={["CROSSWORD"]}
                                        showTags={false}
                                        showFreePaidBreakdown={state === PUBLISHED}
                                        showCountByType={state === PUBLISHED}
                                        hideTypeLabels={true}
                                    />
                                ))}
                            {statsViewMode === 2 && activeStep === 0 && (
                                <Grid size={{ xs: 12 }}>
                                    <CommonTagByStateStatsPanel
                                        stateKeys={DISPLAY_STATES}
                                        groupByStateStats={groupByStateStats}
                                        tagCountsByApi={commonTagCountsApiData}
                                        apiLoading={commonTagCountsApiLoading}
                                        apiFailed={commonTagCountsApiFailed}
                                    />
                                </Grid>
                            )}
                            {statsViewMode === 3 &&
                                [
                                    {
                                        state: "READY_TO_REVIEW_REVIEWED_PUBLISHED",
                                        stateLabelOverride: "READY TO REVIEW / REVIEWED / PUBLISHED",
                                        stateData: mergeStateStats(groupByStateStats, [
                                            READY_TO_REVIEW,
                                            REVIEWED,
                                            PUBLISHED
                                        ])
                                    }
                                ].map(({ state, stateData, stateLabelOverride }) => (
                                    <StateStatsCard
                                        key={state}
                                        state={state}
                                        stateData={stateData}
                                        typesFilter={["CROSSWORD"]}
                                        stateLabelOverride={stateLabelOverride}
                                        showTags={true}
                                        tagsOnly={true}
                                        excludeTags={["bedrock"]}
                                        gridSize={{ xs: 12 }}
                                        onTagClick={(tag) => handleTagsChange([tag])}
                                        hint="Choose tags with low counts and create puzzles for them—we need more puzzles in those categories."
                                    />
                                ))}
                        </Grid>
                    ) : null}
                </Box>

                <Step0SelectDraft
                    key={`step-${activeStep}`} // Add key to force re-render when step changes
                    step={activeStep}
                    stepState={steps[activeStep].state}
                    selectedGenreId="all"
                    selectedTags={selectedTags}
                    availableTags={availableTags}
                    tagsLoading={tagsLoading}
                    selectedLevel={selectedLevel}
                    selectedFree={selectedFree}
                    onTagsChange={handleTagsChange}
                    onLevelChange={handleLevelChange}
                    onFreeChange={handleFreeChange}
                    initialKeyword={savedKeyword}
                    onKeywordChange={handleKeywordChange}
                    handlePuzzleClick={handlePuzzleClick}
                />

                <Box sx={{ display: "flex", flexDirection: "row", mt: 2 }}>
                    <Button
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                    >
                        Back
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />
                    {activeStep < steps.length - 1 && (
                        <Button onClick={handleNext} variant="contained">
                            Next
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Current Step: {activeStep + 1} of {steps.length} - {steps[activeStep].label}
                </Typography>
            </Box>

            {/* Publishing Policy Dialog */}
            <PublishingPolicyDialog
                open={policyDialogOpen}
                onClose={() => setPolicyDialogOpen(false)}
            />
        </Box>
    );
}

